import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../lib/api";
import { STAGES, FAITH_STATUSES, DBS_TYPES } from "../lib/constants";
import { PersonForm, DbsChip } from "./People";
import { Modal } from "../components/Modal";

interface Person {
  id: string; name: string; email?: string | null; phone?: string | null;
  source: string | null; pathway_stage: string; faith_status: string;
  group_id: string | null; group_name?: string;
  assigned_leader_id?: string | null;
  dbs_type?: string | null;
  notes?: string | null;
}

export default function Pathway() {
  const [people, setPeople] = useState<Person[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [leaders, setLeaders] = useState<{ id: string; name: string }[]>([]);
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [faithFilter, setFaithFilter] = useState<string>("");
  const [dbsFilter, setDbsFilter] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Tracks whether the most recent mouse interaction was a drag.
  // We use this to suppress the click-to-edit when the user actually dragged.
  const wasDragged = useRef(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (groupFilter) params.set("group_id", groupFilter);
    if (faithFilter) params.set("status", faithFilter);
    if (dbsFilter) params.set("dbs_type", dbsFilter);
    const [p, g, l] = await Promise.all([
      api.get<{ people: Person[] }>(`/people?${params}`),
      api.get<{ groups: any[] }>("/groups"),
      api.get<{ leaders: any[] }>("/leaders"),
    ]);
    setPeople(p.people);
    setGroups(g.groups);
    setLeaders(l.leaders);
  };
  useEffect(() => { load(); }, [groupFilter, faithFilter, dbsFilter]);

  const move = async (id: string, stage: string) => {
    setPeople((cur) => cur.map((p) => p.id === id ? { ...p, pathway_stage: stage } : p));
    await api.patch(`/people/${id}`, { pathway_stage: stage });
  };

  const onCardClick = (p: Person) => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    setEditing(p);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Discipleship Pathway</h1>
          <p className="text-ink-500 text-sm">Click a card to edit · drag to move between stages</p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Add Person</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <select className="input" value={faithFilter} onChange={(e) => setFaithFilter(e.target.value)}>
          <option value="">All Faith Status</option>
          {FAITH_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className="input" value={dbsFilter} onChange={(e) => setDbsFilter(e.target.value)}>
          <option value="">All DBS Types</option>
          {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select className="input" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
          <option value="">All Groups</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {/*
        Desktop: 5-column grid. Mobile: horizontal snap-scroll where each
        column is ~85vw so the next one peeks in. Cards are tap-to-edit on
        mobile (drag is keyboard/desktop only — no touch drag UX needed).
      */}
      <div className="
        grid md:grid-cols-5 md:gap-3
        max-md:flex max-md:overflow-x-auto max-md:gap-3 max-md:snap-cols
        max-md:-mx-4 max-md:px-4 max-md:pb-2
      ">
        {STAGES.map((stage) => {
          const list = people.filter((p) => p.pathway_stage === stage.id);
          return (
            <div
              key={stage.id}
              className={`
                rounded-xl p-3 ${stage.color}
                md:min-h-[300px]
                max-md:snap-col-item max-md:shrink-0 max-md:w-[85vw] max-md:min-h-[60vh]
              `}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingId) {
                  wasDragged.current = true;
                  move(draggingId, stage.id);
                  setDraggingId(null);
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase">{stage.label}</div>
                <span className="badge bg-white/70">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.map((p) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => { wasDragged.current = false; setDraggingId(p.id); }}
                    onDrag={() => { wasDragged.current = true; }}
                    onDragEnd={() => setDraggingId(null)}
                    onClick={() => onCardClick(p)}
                    className="bg-white rounded-lg p-3 shadow-sm cursor-pointer ring-1 ring-ink-100 hover:ring-brand hover:shadow-md transition"
                    title="Tap to edit · drag to move (desktop)"
                  >
                    <div className="font-medium text-ink-900 text-sm">{p.name}</div>
                    {p.source && <div className="text-xs text-ink-500 mt-0.5">{p.source}</div>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      <FaithBadge id={p.faith_status} />
                      {p.dbs_type && <DbsChip id={p.dbs_type} />}
                      {p.group_name && <span className="badge bg-ink-100 text-ink-700">{p.group_name}</span>}
                    </div>
                  </div>
                ))}
                {list.length === 0 && <div className="text-xs text-ink-400 italic px-1">No one here yet</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile-only swipe hint */}
      <div className="md:hidden flex items-center justify-center gap-1.5 -mt-3 text-xs text-ink-400">
        ← Swipe to see all {STAGES.length} stages →
      </div>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add Person" wide>
        <PersonForm
          groups={groups}
          leaders={leaders}
          onCreated={() => { setAdding(false); load(); }}
          onCancel={() => setAdding(false)}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Person" wide>
        {editing && (
          <PersonForm
            groups={groups}
            leaders={leaders}
            initial={editing}
            onCreated={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
            onDelete={async () => {
              if (!confirm(`Delete ${editing.name}? This cannot be undone.`)) return;
              await api.del(`/people/${editing.id}`);
              setEditing(null); load();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function FaithBadge({ id }: { id: string }) {
  const f = FAITH_STATUSES.find((s) => s.id === id);
  if (!f) return null;
  return (
    <span className="badge bg-ink-50 text-ink-700">
      <span className={`h-1.5 w-1.5 rounded-full mr-1 ${f.dot}`} />
      {f.label}
    </span>
  );
}
